import { get, patch, post } from 'aws-amplify/api';
import { processResponse } from './api_util';

const API_NAME = 'LivisiblyProspectsApi';

/**
 * List prospects with optional filters.
 *
 * @param {Object} filters
 * @param {string[]} [filters.stages]           - e.g. ['NEW', 'CONTACTED']
 * @param {string[]} [filters.personas]         - e.g. ['consumer', 'professional']
 * @param {string[]} [filters.sources]          - e.g. ['form_demo', 'manual']
 * @param {boolean}  [filters.consentMarketing] - true/false/undefined (all)
 * @param {boolean}  [filters.demoCompleted]    - true/false/undefined (all)
 * @param {string}   [filters.dateFrom]         - ISO date string
 * @param {string}   [filters.dateTo]           - ISO date string
 * @param {number}   [filters.limit]            - max 500, default 100
 * @param {number}   [filters.offset]           - pagination offset
 * @returns {Promise<{prospects: Object[], count: number}>}
 */
export async function listProspects(filters = {}) {
  const {
    stages,
    personas,
    sources,
    consentMarketing,
    demoCompleted,
    dateFrom,
    dateTo,
    limit = 100,
    offset = 0,
  } = filters;

  const queryParams = { limit: String(limit), offset: String(offset) };
  if (stages?.length) queryParams.stages = stages.join(',');
  if (personas?.length) queryParams.personas = personas.join(',');
  if (sources?.length) queryParams.sources = sources.join(',');
  if (consentMarketing != null) queryParams.consentMarketing = String(consentMarketing);
  if (demoCompleted != null) queryParams.demoCompleted = String(demoCompleted);
  if (dateFrom) queryParams.dateFrom = dateFrom;
  if (dateTo) queryParams.dateTo = dateTo;

  const restOperation = get({
    apiName: API_NAME,
    path: 'prospects',
    options: { queryParams },
  });
  return processResponse(await restOperation.response);
}

/**
 * Get a single prospect with its activity timeline.
 *
 * @param {string} prospectId
 * @returns {Promise<{prospect: Object, activities: Object[]}>}
 */
export async function getProspect(prospectId) {
  const restOperation = get({
    apiName: API_NAME,
    path: `prospects/${prospectId}`,
  });
  return processResponse(await restOperation.response);
}

/**
 * Change a prospect's stage (human-driven; may go any direction).
 *
 * @param {string} prospectId
 * @param {string} stage      - must be a valid VALID_STAGES value
 * @param {string} [summary]  - optional note for the activity log
 * @returns {Promise<{prospect: Object}>}
 */
export async function patchProspectStage(prospectId, stage, summary) {
  const body = { stage };
  if (summary) body.summary = summary;
  const restOperation = patch({
    apiName: API_NAME,
    path: `prospects/${prospectId}/stage`,
    options: { body },
  });
  return processResponse(await restOperation.response);
}

/**
 * Mark a prospect's demo as completed.
 *
 * @param {string} prospectId
 * @returns {Promise<{prospect: Object}>}
 */
export async function patchProspectDemo(prospectId) {
  const restOperation = patch({
    apiName: API_NAME,
    path: `prospects/${prospectId}/demo`,
    options: { body: {} },
  });
  return processResponse(await restOperation.response);
}

/**
 * Add a free-text note to a prospect's activity timeline.
 *
 * @param {string} prospectId
 * @param {string} note       - plain text, max 5000 chars
 * @returns {Promise<{activity: Object}>}
 */
export async function postProspectNote(prospectId, note) {
  const restOperation = post({
    apiName: API_NAME,
    path: `prospects/${prospectId}/notes`,
    options: { body: { note } },
  });
  return processResponse(await restOperation.response);
}

/**
 * Send a signup-link invite email to a prospect.
 *
 * @param {string} prospectId
 * @returns {Promise<{message: string}>}
 */
export async function postProspectInvite(prospectId) {
  const restOperation = post({
    apiName: API_NAME,
    path: `prospects/${prospectId}/invite`,
    options: { body: {} },
  });
  return processResponse(await restOperation.response);
}
